import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useUsageCompliance } from '../hooks/useReports';
import { exportToCSV } from '@/shared/utils/exportUtils';
import { formatDate } from '@/shared/utils/dateUtils';
import type { UsageComplianceFilters } from '../services/reportsService';

interface UsageComplianceSectionProps {
  filters: UsageComplianceFilters;
}

export function UsageComplianceSection({ filters }: UsageComplianceSectionProps) {
  const { data, isLoading, error } = useUsageCompliance(filters);

  const getUsageBadgeVariant = (usagePercentage: number) => {
    if (usagePercentage >= 70) return 'success';
    if (usagePercentage >= 40) return 'warning';
    return 'danger';
  };

  const getActivityLevel = (entriesPerDay: number) => {
    if (entriesPerDay >= 5) return 'High';
    if (entriesPerDay >= 2) return 'Medium';
    if (entriesPerDay >= 0.5) return 'Low';
    return 'Very Low';
  };

  const handleExport = () => {
    if (!data) return;

    exportToCSV(
      data.map((item) => ({
        Location: item.location_name,
        'Working Days in Range': item.date_range_days,
        'Working Days with Entries': item.days_with_entries,
        'Total Entries': item.total_entries,
        'Entries per Active Day': item.entries_per_active_day.toFixed(1),
        'Usage %': item.usage_percentage.toFixed(1),
      })),
      'usage-compliance-report'
    );
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Usage & Compliance</h2>
        <div className="text-center py-8 text-gray-500">Loading...</div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Usage & Compliance</h2>
        <div className="text-center py-8 text-red-600">
          Error loading data: {error.message}
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Usage & Compliance</h2>
        <div className="text-center py-8 text-gray-500">
          No data found for the selected filters
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Usage & Compliance</h2>
          <p className="text-sm text-gray-500 mt-1">
            Showing data from {formatDate(filters.date_from)} to {formatDate(filters.date_to)}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {data.map((location) => (
          <Card key={location.location_id} className="p-4 border-2">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-semibold">{location.location_name}</h3>
              <Badge variant={getUsageBadgeVariant(location.usage_percentage)}>
                {location.usage_percentage.toFixed(0)}%
              </Badge>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Working Days with Entries:</span>
                <span className="font-medium">
                  {location.days_with_entries} / {location.date_range_days}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Entries:</span>
                <span className="font-medium">{location.total_entries}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Avg per Active Day:</span>
                <span className="font-medium">
                  {location.entries_per_active_day.toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Activity Level:</span>
                <span
                  className={`font-medium ${
                    getActivityLevel(location.entries_per_active_day) === 'High'
                      ? 'text-green-600'
                      : getActivityLevel(location.entries_per_active_day) === 'Medium'
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {getActivityLevel(location.entries_per_active_day)}
                </span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Working Days
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Active Days
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Entries
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avg/Day
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usage %
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Activity Level
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((location) => (
              <tr key={location.location_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-medium">
                  <span className="hidden lg:inline">{location.location_name}</span>
                  <span className="lg:hidden">{location.location_short_code || location.location_name}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {location.date_range_days}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                  {location.days_with_entries}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600 font-medium">
                  {location.total_entries}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {location.entries_per_active_day.toFixed(1)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <Badge variant={getUsageBadgeVariant(location.usage_percentage)}>
                    {location.usage_percentage.toFixed(1)}%
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <span
                    className={`font-medium ${
                      getActivityLevel(location.entries_per_active_day) === 'High'
                        ? 'text-green-600'
                        : getActivityLevel(location.entries_per_active_day) === 'Medium'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}
                  >
                    {getActivityLevel(location.entries_per_active_day)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <h4 className="font-semibold text-blue-900 mb-2">Understanding Usage %</h4>
        <p className="text-sm text-blue-900">
          <strong>Usage %:</strong> Percentage of <em>working days</em> (Monday–Friday only)
          in the selected date range that had at least one frame entry (PENDING, ORDERED, or RECEIVED).
        </p>
        <p className="text-sm text-blue-900 mt-2">
          <strong>Note:</strong> Weekends (Saturday, Sunday) are excluded from the denominator
          and do not count as eligible usage days.
        </p>
        <div className="mt-3 space-y-1">
          <p className="text-sm text-blue-900">
            <strong>Usage Interpretation:</strong>
          </p>
          <ul className="text-sm text-blue-900 ml-4 list-disc">
            <li>≥ 70% = High usage (strong system adoption)</li>
            <li>40–69% = Moderate usage (needs occasional coaching)</li>
            <li>&lt; 40% = Low usage (possible compliance issue or operational gap)</li>
          </ul>
        </div>
        <p className="text-sm text-blue-900 mt-3">
          <strong>Activity Level:</strong> Based on average entries per active day.
          High (≥5/day), Medium (2-5/day), Low (&lt;2/day).
        </p>
      </div>
    </Card>
  );
}
